import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import css from "./Contact.module.css";
import Image from "../../Assets/Images/Sidebar.png";
import { Post, Get } from "../../Services/Server";
import User from "../../Services/User";
import Url from "../../Services/Url";
import ContactFormOptions from "./EnterpriseContactFormOptions";
import SiteDocumentationFormOptions from "./SiteDocumentationFormOptions";
import PowerSourcesFormOptions from "./PowerSourcesFormOptions";
import RouterLocationFormOptions from "./RouterLocationFormOptions";
import Form from "../../Components/Form2/Form";
import Button from "../../Components/Button/Button";
import { ReactComponent as CheckIcon } from "../../Assets/svg/check.svg";
import { ReactComponent as CheckCircleIcon } from "../../Assets/svg/check_circle.svg";
import { ReactComponent as InfoIcon } from "../../Assets/svg/info.svg";

const steps = [
	{ id: 1, title: "Personal Details" },
	{ id: 2, title: "Site Documentation" },
	{ id: 3, title: "Power Sources" },
	{ id: 4, title: "Router Location" },
];

const EnterpriseContact = () => {
	const { state } = useLocation();
	const navigate = useNavigate();
	const [contactStepNumber, setContactStepNumber] = useState(1);
	const [formData, setFormData] = useState({});
	const init = User?.getJobInit();
	const localStorageKey = `surveyFormData_${init?.initialId}`;

	useEffect(() => {
		const savedData = localStorage.getItem(localStorageKey);
		if (savedData) {
			const parsedData = JSON.parse(savedData);
			setFormData(parsedData.formData);
			setContactStepNumber(parsedData.contactStepNumber);
		} else {
			setFormData({
				companyName: init?.companyName || "",
				firstname: init?.firstname || "",
				lastname: init?.lastname || "",
				phone: init?.phone || "",
				email: init?.email || "",
				siteType: 1,
			});
		}
	}, [init?.initialId, localStorageKey]);

	useEffect(() => {
		localStorage.setItem(
			localStorageKey,
			JSON.stringify({ formData, contactStepNumber })
		);
	}, [formData, contactStepNumber, localStorageKey]);

	useEffect(() => {
		if (init?.initialId) {
			Get(Url.client.surveyByJobId(init.initialId))
				.then((res) => {
					if (Array.isArray(res.data) && res.data.length > 0) {
						setContactStepNumber(steps.length + 1);
					}
				})
				.catch((err) => {
					console.log("Survey check failed:", err);
				});
		}
	}, [init?.initialId]);

	const isCompleted = contactStepNumber > steps.length;
	let title = steps[contactStepNumber - 1]?.title || "";
	let formOptions;

	switch (contactStepNumber) {
		case 1:
			formOptions = ContactFormOptions(formData);
			break;
		case 2:
			formOptions = SiteDocumentationFormOptions(formData);
			break;
		case 3:
			formOptions = PowerSourcesFormOptions(formData);
			break;
		case 4:
			formOptions = RouterLocationFormOptions(formData);
			break;
		default:
			formOptions = ContactFormOptions(formData);
	}

	const handleFormChange = (e) => {
		const { name, value, checked, type } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	const onClickGoBack = () => {
		if (contactStepNumber > 1) {
			setContactStepNumber(contactStepNumber - 1);
		}
	};

	const validateFields = (formOptions, formData) => {
		const flatFields = formOptions.Fields.flat();
		for (const field of flatFields) {
			if (field?.required && !formData[field.name]) {
				toast.error(`${field.label || field.name} is required`);
				return false;
			}
		}
		return true;
	};

	const onClickContinue = async () => {
		if (!validateFields(formOptions, formData)) return;

		if (contactStepNumber < 4) {
			if (contactStepNumber === 1) {
				const contactData = {
					companyName: formData.companyName,
					firstname: formData.firstname,
					lastname: formData.lastname,
					phone: formData.phone,
					email: formData.email,
					companyType: "e",
				};

				try {
					await Post(Url.client.contact, contactData);
					setContactStepNumber(contactStepNumber + 1);
				} catch (err) {
					console.error(err);
					toast.error("An error occurred while saving contact info.");
				}
			} else {
				setContactStepNumber(contactStepNumber + 1);
			}
		} else {
			const payload = createSurveyPayload(formData);

			try {
				await Post(Url.client.survey, payload);
				toast.success("Survey submitted successfully!");
				setContactStepNumber(steps.length + 1);
				localStorage.removeItem(localStorageKey);
			} catch (err) {
				console.error(err);
				toast.error("An error occurred while submitting the survey.");
			}
		}
	};

	const createSurveyPayload = (data) => {
		const siteLocations = [
			{
				locationName: data.primaryLocationName,
				siteDoc: data.primaryLocation || [],
				power: [
					{
						siteLocDis: data.distancePowerSource1,
						powDoc: data.powerSource1 || [],
					},
				],
			},
		];

		if (data.secondaryLocationName || data.secondaryLocation) {
			siteLocations.push({
				locationName: data.secondaryLocationName,
				siteDoc: data.secondaryLocation || [],
				power: [
					{
						siteLocDis: data.distancePowerSource2,
						powDoc: data.powerSource2 || [],
					},
				],
			});
		}

		return {
			job_id: init.initialId,
			site_addr: data.siteAddress,
			obstruction: data.obstructionDescription?.length > 0,
			scaffload: data.scaffoldRequired === "yes",
			ladder: data.ladderRequired === "yes",
			trunc: data.trunkingRequired === "yes",
			pow_back_up: data.backupPowerSource,
			cable_desp: data.cableRouteDescription,
			cable_length: Number(data.cableLengthEstimate) || 0,
			site_type: data.siteType ? { id: data.siteType } : null,
			site_loc: siteLocations,
			cable_route: data.plannedCableRoute
				? [{ docId: data.plannedCableRoute }]
				: [],
			router_details: {
				description: data.routerLocationDescription,
				docid: data.routerLocationImage || [],
				routerstorage: data.routerStorageAvailable === "yes",
				penetration: data.drillingRequired === "yes",
			},
		};
	};

	const progressPercent = ((contactStepNumber - 1) / (steps.length - 1)) * 100;
	const isSubmitDisabled = contactStepNumber === 4 && !formData?.acknowledge;

	return (
		<div className={css.PageWrap}>
			<div className={css.LeftPanel}>
				<div className={css.Content} />
				<img className={css.Image} src={Image} alt="Background" />
			</div>

			<div className={css.RightPanel}>
				<div className={css.Stepper}>
					<div className={css.StepperTrack} />
					<div
						className={css.StepperProgress}
						style={{ width: `${isCompleted ? 100 : progressPercent}%` }}
					/>
					{steps.map((s) => (
						<div key={s.id} className={css.StepItem}>
							<div
								className={`${css.StepCircle} ${
									isCompleted || contactStepNumber >= s.id ? css.Active : ""
								}`}
								aria-current={contactStepNumber === s.id}
							>
								{isCompleted || contactStepNumber > s.id ? (
									<CheckIcon className={css.CheckIcon} />
								) : (
									s.id
								)}
							</div>
							<div
								className={`${css.StepTitle} ${
									isCompleted || contactStepNumber >= s.id
										? css.ActiveTitle
										: ""
								}`}
							>
								{s.title}
							</div>
						</div>
					))}
				</div>

				<div className={css.FormContent}>
					{isCompleted ? (
						<div className={css.CompletedBox}>
							<CheckCircleIcon className={css.CheckCircle} />
							<h2 className={css.Title}>
								Thank you for completing the survey.
							</h2>
							<p className={css.Details}>
								Your details have been received and will help us prepare for a
								smooth installation. We'll be in touch soon!
							</p>
						</div>
					) : (
						<>
							<h2 className={css.FormTitle}>{title}</h2>
							<div className={css.FormWrapper}>
								<Form onChange={handleFormChange}>{formOptions}</Form>
							</div>
						</>
					)}
				</div>

				{!isCompleted && (
					<div className={css.Buttons}>
						{contactStepNumber > 1 && (
							<Button className={css.BtnCancel} onClick={onClickGoBack}>
								Back
							</Button>
						)}
						<Button
							className={`${css.BtnContinue} ${
								isSubmitDisabled ? css.DisabledBtn : ""
							}`}
							onClick={onClickContinue}
							disabled={isSubmitDisabled}
						>
							{contactStepNumber === 4 ? "Submit" : "Continue"}
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

export default EnterpriseContact;
